import React, { ReactElement, useRef, useState } from 'react'
import { css } from '@emotion/css'
import { DocumentNode } from 'graphql'
import gql from 'graphql-tag'
import { Mutation } from 'react-apollo'
import { CellRenderer } from '../../legacy-design-system/grid'
import { Role, RoleSwitch } from '../roles'
import { OptimisticToggleManager } from '../../legacy-design-system/toggle'
import { LoadingTextMask } from '../../legacy-design-system/loading-masks'
import { AgentFeature, isFeatureEnabledForLanguage } from '../agent-features'
import { getProductToggleDisabledMsg, serverFragment } from './shared'
import { PureQueryOptions } from 'apollo-client'
import { WithTooltip } from '../../design-system'
import { logError, logSuccess } from '../toaster'
import { AgentLanguage } from '../agent-language'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import { invalidateVrLicenseQueries } from '../value-reporting/modules/licenses/api/queries/vr-licenses'
import { QueryClient } from 'react-query'
import { ServerUpdatingFeature } from './grid'
import { Toggle } from '@contrast-security-inc/design-system-inputs'
import { IconButton } from '@contrast-security-inc/design-system-buttons'
import { IconInformation } from '@contrast-security-inc/design-system-foundations'
import { ServerProductConfigPopover } from '../server-product-config-popover/server-product-config-popover'
import { ContrastProduct } from '../shared'

const tooltipContainerCss = css`
  max-width: 200px;
`

interface FeatureSwitchCellProps {
  feature: ContrastProduct
  blocked?: boolean
  propertyName?: string
  propertyLocation?: string
  disableToggle: boolean
  disabledMessage?: string
  featureIsEnabled: boolean
  getCellContentProps: (props: {}) => {
    className?: string
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
  }
  organizationRole: Role
  requiredRole: Role
  organizationID: string
  serverID: string
  mutation: DocumentNode
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  successMessage?: string
  errorMessage?: string
  setServerUpdatingFeature: (
    serverUpdatingFeature: ServerUpdatingFeature | null
  ) => void
}

function FeatureSwitchCell({
  feature,
  blocked = false,
  propertyName,
  propertyLocation,
  disableToggle,
  disabledMessage,
  featureIsEnabled,
  getCellContentProps,
  organizationRole,
  requiredRole,
  mutation,
  organizationID,
  serverID,
  refetchQueries,
  refresh,
  successMessage,
  errorMessage,
  setServerUpdatingFeature
}: FeatureSwitchCellProps) {
  return (
    <Mutation mutation={mutation}>
      {(setFeature) => (
        <div {...getCellContentProps({})}>
          <ToggleWrapper
            feature={feature}
            featureIsEnabled={featureIsEnabled}
            disableToggle={disableToggle}
            disabledMessage={disabledMessage}
            showToggleDisabledIcon={blocked}
            propertyName={propertyName}
            propertyLocation={propertyLocation}
          >
            <RoleSwitch
              role={organizationRole}
              requiredRole={requiredRole}
              renderAllowed={() => (
                <OptimisticToggleManager checked={featureIsEnabled}>
                  {({ checked, isUpdating, setIsUpdating }) => {
                    const isDisabled = disableToggle || isUpdating || blocked

                    return (
                      <Toggle
                        aria-label={`server-${feature}-toggle`}
                        testId={`server-${feature}-toggle`}
                        className={
                          blocked
                            ? css`
                                margin-right: 16px;
                              `
                            : undefined
                        }
                        isDisabled={isDisabled}
                        isChecked={checked}
                        onChange={() => {
                          setIsUpdating(true)
                          setServerUpdatingFeature({
                            feature,
                            id: serverID
                          })
                          return setFeature({
                            variables: {
                              organizationID,
                              id: serverID,
                              enabled: !featureIsEnabled
                            },
                            refetchQueries
                          })
                            .then(() => {
                              if (successMessage) {
                                logSuccess(successMessage)
                              }
                            })
                            .catch((error) => {
                              console.error(error)
                              if (errorMessage) {
                                logError(errorMessage)
                              }

                              return error
                            })
                            .finally(() => {
                              setServerUpdatingFeature(null)
                              setIsUpdating(false)
                              return refresh()
                            })
                        }}
                      />
                    )
                  }}
                </OptimisticToggleManager>
              )}
              renderDenied={() => (
                <Toggle
                  testId={`server-${feature}-toggle`}
                  className={
                    blocked
                      ? css`
                          margin-right: 16px;
                        `
                      : undefined
                  }
                  isDisabled
                  isChecked={featureIsEnabled}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onChange={() => {}}
                />
              )}
            />
          </ToggleWrapper>
        </div>
      )}
    </Mutation>
  )
}

interface GetAssessProps {
  organizationRole: Role
  agentFeatures: AgentFeature[]
  organizationID: string
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  serverUpdatingFeature: ServerUpdatingFeature | null
  setServerUpdatingFeature: (
    serverUpdatingFeaturea: ServerUpdatingFeature | null
  ) => void
}

export interface AssessRowData {
  language: string | null
  assess: boolean
  id: string
  protect: boolean
  isAssessStatusLocked?: boolean
  assessEnablePropertyName?: string
  assessEnableLocation?: string
  agentVersion: string
}

const setAssessMutation = gql`
  mutation SetServerAssess($organizationID: ID!, $id: ID!, $enabled: Boolean!) {
    setServerAssess(
      input: { organizationID: $organizationID, id: $id, enabled: $enabled }
    ) {
      ...Server
    }
  }

  ${serverFragment}
`

export function getAssessCellRenderer({
  organizationRole,
  agentFeatures,
  organizationID,
  refetchQueries,
  refresh,
  serverUpdatingFeature,
  setServerUpdatingFeature
}: GetAssessProps): CellRenderer<AssessRowData> {
  return function AssessCellRenderer({ rowData, getCellContentProps }) {
    const {
      assess,
      language,
      id,
      isAssessStatusLocked: blocked,
      assessEnableLocation: propertyLocation,
      assessEnablePropertyName: propertyName
    } = rowData

    const agentLanguage = !!language && AgentLanguage.fromValue(language)

    const featureIsDisabledForLanguage =
      agentLanguage &&
      !isFeatureEnabledForLanguage({
        agentFeatures,
        language: agentLanguage,
        featureName: 'assess'
      })

    const isUpdatingProtect = () =>
      serverUpdatingFeature &&
      serverUpdatingFeature.id === id &&
      serverUpdatingFeature.feature === 'PROTECT'

    const successMessageProps = {
      id: 'select_servers_successfullyToggledAssess',
      values: { assess: !assess }
    }

    const errorMessageProps = {
      id: 'select_servers_errorTogglingAssess',
      values: { assess: !assess }
    }

    return (
      <ContrastFormattedMessage {...successMessageProps}>
        {(successMessage) => (
          <ContrastFormattedMessage {...errorMessageProps}>
            {(errorMessage) => (
              <FeatureSwitchCell
                feature="ASSESS"
                blocked={blocked}
                propertyName={propertyName}
                propertyLocation={propertyLocation}
                disableToggle={
                  isUpdatingProtect() ||
                  !agentLanguage ||
                  featureIsDisabledForLanguage
                }
                getCellContentProps={getCellContentProps}
                featureIsEnabled={assess}
                organizationRole={organizationRole}
                requiredRole="ADMIN"
                organizationID={organizationID}
                serverID={id}
                mutation={setAssessMutation}
                refetchQueries={refetchQueries}
                refresh={refresh}
                successMessage={successMessage}
                errorMessage={errorMessage}
                setServerUpdatingFeature={setServerUpdatingFeature}
              />
            )}
          </ContrastFormattedMessage>
        )}
      </ContrastFormattedMessage>
    )
  }
}

export interface GetProtectProps {
  organizationRole: Role
  agentFeatures: AgentFeature[]
  protectLicenseBreakdown?: {
    used: number
    unused: number
  }
  organizationID: string
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  queryClient: QueryClient
  serverUpdatingFeature: ServerUpdatingFeature | null
  setServerUpdatingFeature: (
    serverUpdatingFeaturea: ServerUpdatingFeature | null
  ) => void
}

export interface ProtectRowData {
  language: string | null
  protect: boolean
  license: {}
  id: string
  assess: boolean
  isProtectStatusLocked?: boolean
  protectEnablePropertyName?: string
  protectEnableLocation?: string
  agentVersion: string
}

const setProtectMutation = gql`
  mutation SetServerProtect(
    $organizationID: ID!
    $id: ID!
    $enabled: Boolean!
  ) {
    setServerProtect(
      input: { organizationID: $organizationID, id: $id, enabled: $enabled }
    ) {
      ...Server
    }
  }

  ${serverFragment}
`

export function getProtectCellRenderer({
  organizationRole,
  agentFeatures,
  organizationID,
  refetchQueries,
  refresh,
  queryClient,
  serverUpdatingFeature,
  setServerUpdatingFeature
}: GetProtectProps): CellRenderer<ProtectRowData> {
  return function ProtectCellRenderer({ rowData, getCellContentProps }) {
    const {
      protect,
      language,
      id,
      isProtectStatusLocked: blocked,
      protectEnableLocation: propertyLocation,
      protectEnablePropertyName: propertyName
    } = rowData
    const agentLanguage = !!language && AgentLanguage.fromValue(language)

    const featureIsDisabledForLanguage =
      agentLanguage &&
      !isFeatureEnabledForLanguage({
        agentFeatures,
        language: agentLanguage,
        featureName: 'protection'
      })

    const isUpdatingAssess = () =>
      serverUpdatingFeature &&
      serverUpdatingFeature.id === id &&
      serverUpdatingFeature.feature === 'ASSESS'

    const successMessageProps = {
      id: 'select_servers_successfullyToggledProtect',
      values: { protect: !protect }
    }

    const errorMessageProps = {
      id: 'select_servers_errorTogglingProtect',
      values: { protect: !protect }
    }

    return (
      <ContrastFormattedMessage
        {...geti18nDisabledProtectMsg(language, 'PROTECT')}
      >
        {(disabledMessage) => (
          <ContrastFormattedMessage {...successMessageProps}>
            {(successMessage) => (
              <ContrastFormattedMessage {...errorMessageProps}>
                {(errorMessage) => (
                  <FeatureSwitchCell
                    feature="PROTECT"
                    blocked={blocked}
                    propertyName={propertyName}
                    propertyLocation={propertyLocation}
                    disableToggle={
                      isUpdatingAssess() ||
                      !agentLanguage ||
                      featureIsDisabledForLanguage
                    }
                    disabledMessage={
                      isUpdatingAssess() ? undefined : disabledMessage
                    }
                    getCellContentProps={getCellContentProps}
                    featureIsEnabled={protect}
                    organizationRole={organizationRole}
                    requiredRole="RULES_ADMIN"
                    organizationID={organizationID}
                    serverID={id}
                    mutation={setProtectMutation}
                    refetchQueries={refetchQueries}
                    refresh={() => {
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      invalidateVrLicenseQueries(queryClient)
                      return refresh()
                    }}
                    successMessage={successMessage}
                    errorMessage={errorMessage}
                    setServerUpdatingFeature={setServerUpdatingFeature}
                  />
                )}
              </ContrastFormattedMessage>
            )}
          </ContrastFormattedMessage>
        )}
      </ContrastFormattedMessage>
    )
  }
}

function ToggleWrapper({
  children,
  showToggleDisabledIcon,
  feature,
  propertyLocation,
  propertyName,
  disableToggle,
  disabledMessage,
  featureIsEnabled
}: {
  children: ReactElement
  showToggleDisabledIcon: boolean
  feature: ContrastProduct
  propertyName?: string
  propertyLocation?: string
  disableToggle: boolean
  disabledMessage?: string
  featureIsEnabled: boolean
}) {
  const [showPopover, setShowPopover] = useState(false)
  const referenceElement = useRef<HTMLButtonElement | null>(null)

  return (
    <>
      <div
        className={css`
          display: flex;
          flex-direction: row;
          min-width: 90px;
          justify-content: center;
        `}
      >
        {showToggleDisabledIcon && (
          <span
            className={css`
              margin-right: 4px;
              align-self: center;
            `}
          >
            <IconButton
              ref={referenceElement}
              testId={`${feature}-toggle-disabled`}
              aria-label="toggle disabled icon button"
              Icon={IconInformation}
              onClick={() => setShowPopover(true)}
              variant="secondary"
              size="sm"
            ></IconButton>
          </span>
        )}

        {disableToggle && disabledMessage ? (
          <WithTooltip
            renderTooltipContent={() => {
              return (
                <div className={tooltipContainerCss}>{disabledMessage}</div>
              )
            }}
          >
            {() => children}
          </WithTooltip>
        ) : (
          children
        )}
      </div>
      {showPopover && (
        <ServerProductConfigPopover
          product={feature}
          isProductOn={featureIsEnabled}
          propertyName={propertyName || ''}
          propertyLocation={propertyLocation || ''}
          referenceElement={referenceElement}
          onClose={() => setShowPopover(false)}
        />
      )}
    </>
  )
}

export function featureSwitchLoadingCellRenderer() {
  return (
    <div>
      <LoadingTextMask min={3} max={3} />
    </div>
  )
}

export const geti18nDisabledProtectMsg = (
  language: string | null,
  product: ContrastProduct
) => {
  return language
    ? getProductToggleDisabledMsg(language, product)
    : {
        id: 'detail_servers_hostApplication'
      }
}
